import { useEffect, useMemo, useState } from 'react';
import { CZECH_MONTHS } from '../data/czechCalendar';
import { createMagicDraft } from '../lib/createMagicDraft';
import { usePhotoPreviewUrls } from '../lib/usePhotoPreviewUrls';
import { loadLastProject, saveProject } from '../lib/projectStorage';
import type { CalendarPage, CalendarPhoto, CalendarProject } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const progressSteps = ['Produkt', 'Fotky', 'Návrh', 'Editor', 'Preflight'];
const productLabels = { basic_year_story: 'Basic Year Story', family_planner_cz: 'Family Planner CZ', fineart_gallery: 'FineArt Gallery' };
const periodLabels = { calendar_year: 'Kalendářní rok 2026', custom_start: 'Od libovolného měsíce', school_year: 'Školní rok září–srpen', anniversary_year: 'Výroční / vlastní rok' };
const themeLabels = { clean_gallery: 'Clean Gallery', warm_family: 'Warm Family', premium_blue: 'Premium Blue' };
const statusLabels = { not_checked: 'Nezkontrolováno', ok: 'OK', warning: 'Warning', error: 'Chybí fotka' };

function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function getPhoto(project: CalendarProject, page: CalendarPage) {
  return project.photos.find((photo) => photo.id === page.photoId);
}

function getUniquePhotoCount(pages: CalendarPage[]) {
  return new Set(pages.map((page) => page.photoId).filter(Boolean)).size;
}

function PagePreview({ photo, previewUrl }: { photo?: CalendarPhoto; previewUrl?: string }) {
  if (photo && previewUrl) {
    return <img src={previewUrl} alt={photo.fileName} />;
  }

  return (
    <div className="draft-no-preview">
      <strong>Lokální fotka není v tomto prohlížeči dostupná</strong>
      <span>Nahraj fotky znovu nebo pokračuj v exportu metadat.</span>
    </div>
  );
}

export function DraftFlow() {
  const [project, setProject] = useState<CalendarProject | null>(null);
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const previewUrls = usePhotoPreviewUrls(project);

  useEffect(() => {
    const loadedProject = loadLastProject();
    if (!loadedProject) {
      setProject(null);
      return;
    }

    const selectedPhotos = loadedProject.photos.filter((photo) => photo.selectedForDraft);
    if (!loadedProject.pages.length && selectedPhotos.length) {
      const draftProject = createMagicDraft(loadedProject);
      saveProject(draftProject);
      setProject(draftProject);
      return;
    }

    setProject(loadedProject);
  }, []);

  const selectedPhotos = useMemo(() => project?.photos.filter((photo) => photo.selectedForDraft) ?? [], [project]);
  const coverPage = project?.pages.find((page) => page.type === 'cover');
  const monthPages = project?.pages.filter((page) => page.type === 'month') ?? [];
  const warningsCount = project?.pages.filter((page) => page.preflightStatus === 'warning' || page.preflightStatus === 'error').length ?? 0;
  const activePage = project?.pages.find((page) => page.id === activePageId);

  const regenerateDraft = () => {
    if (!project) return;
    const draftProject = createMagicDraft({ ...project, pages: [] });
    saveProject(draftProject);
    setProject(draftProject);
    setActivePageId(null);
  };

  const assignPhotoToPage = (pageId: string, photoId: string) => {
    if (!project) return;
    const photo = project.photos.find((item) => item.id === photoId);
    const pages = project.pages.map((page) => {
      if (page.id !== pageId) return page;
      return {
        ...page,
        photoId,
        preflightStatus: photo ? (photo.score >= 75 ? 'ok' : 'warning') : 'error',
      } satisfies CalendarPage;
    });
    const photos = project.photos.map((item) => ({
      ...item,
      assignedPageId: item.id === photoId ? pageId : item.assignedPageId === pageId ? undefined : item.assignedPageId,
    }));
    const updatedProject = { ...project, pages, photos, updatedAt: new Date().toISOString() };
    saveProject(updatedProject);
    setProject(updatedProject);
    setActivePageId(null);
  };

  if (!project) {
    return (
      <main className="draft-shell">
        <section className="draft-empty-state">
          <Logo />
          <h1>Nejdřív vytvoř projekt</h1>
          <p>Magic Draft navazuje na produkt, období a styl ze start flow.</p>
          <Button href="/start">Začít výběrem</Button>
        </section>
      </main>
    );
  }

  if (!selectedPhotos.length) {
    return (
      <main className="draft-shell">
        <section className="draft-empty-state">
          <Logo />
          <h1>Nejdřív nahraj fotky</h1>
          <p>Pro titulku a 12 měsíců potřebujeme vybrané fotky z upload flow.</p>
          <Button href="/upload">Nahrát fotky</Button>
        </section>
      </main>
    );
  }

  return (
    <main className="draft-shell">
      <div className="draft-card">
        <header className="draft-header">
          <Logo />
          <div>
            <a href="/projects">Moje projekty</a>
            <a href="/upload">Zpět k fotkám</a>
            <a href="/">Zpět na homepage</a>
          </div>
        </header>

        <nav className="draft-progress" aria-label="Kroky Magic Flow">
          {progressSteps.map((step, index) => <span key={step} className={index === 2 ? 'is-active' : ''}><small>0{index + 1}</small>{step}</span>)}
        </nav>

        <section className="draft-hero" aria-labelledby="draft-title">
          <p className="eyebrow">NÁVRH V0.1</p>
          <h1 id="draft-title">První návrh kalendáře</h1>
          <p>Z vybraných fotek jsme připravili titulku a 12 měsíců.</p>
        </section>

        <section className="draft-summary-strip" aria-label="Shrnutí návrhu">
          <div><span>Produkt</span><strong>{productLabels[project.productId]}</strong></div>
          <div><span>Období</span><strong>{periodLabels[project.period.mode]}</strong></div>
          <div><span>Styl</span><strong>{themeLabels[project.themeId]}</strong></div>
          <div><span>Vybrané fotky</span><strong>{selectedPhotos.length}</strong></div>
          <div><span>Stav návrhu</span><strong>{warningsCount ? `${warningsCount} upozornění` : 'OK'}</strong></div>
        </section>

        <div className="draft-layout">
          <div className="draft-main">
            {coverPage && (
              <article className={`draft-page-card draft-cover-card status-${coverPage.preflightStatus}`}>
                <div className="draft-page-image"><PagePreview photo={getPhoto(project, coverPage)} previewUrl={previewUrls[coverPage.photoId ?? '']} /></div>
                <div className="draft-page-body">
                  <span className="draft-label">Titulka</span>
                  <h2>{coverPage.title}</h2>
                  <div className="draft-card-meta">
                    {getPhoto(project, coverPage) && <span>Score {getPhoto(project, coverPage)?.score}</span>}
                    <span>{statusLabels[coverPage.preflightStatus]}</span>
                  </div>
                  <button type="button" onClick={() => setActivePageId(coverPage.id)}>Vyměnit fotku</button>
                </div>
              </article>
            )}

            <div className="draft-month-grid">
              {monthPages.map((page) => {
                const photo = getPhoto(project, page);
                return (
                  <article key={page.id} className={`draft-page-card status-${page.preflightStatus}`}>
                    <div className="draft-page-image"><PagePreview photo={photo} previewUrl={previewUrls[page.photoId ?? '']} /></div>
                    <div className="draft-page-body">
                      <span className="draft-label">{page.subtitle}</span>
                      <h3>{page.title}</h3>
                      <div className="draft-card-meta">
                        {photo && <span>Score {photo.score}</span>}
                        <span>{statusLabels[page.preflightStatus]}</span>
                      </div>
                      <button type="button" onClick={() => setActivePageId(page.id)}>Vyměnit fotku</button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <aside className="draft-panel">
            <p className="eyebrow">Magic Draft</p>
            <h2>{project.pages.length} stránek</h2>
            <dl>
              <div><dt>Unikátní fotky</dt><dd>{getUniquePhotoCount(project.pages)}</dd></div>
              <div><dt>Warnings</dt><dd>{warningsCount}</dd></div>
              <div><dt>Začátek</dt><dd>{CZECH_MONTHS[project.period.startMonth - 1]} {project.period.year}</dd></div>
            </dl>
            <button type="button" className="draft-primary" onClick={regenerateDraft}>Přegenerovat návrh</button>
            <button type="button" className="draft-secondary" onClick={() => navigateTo('/editor')}>Pokračovat do editoru</button>
          </aside>
        </div>
      </div>

      {activePage && (
        <div className="draft-modal" role="dialog" aria-modal="true" aria-label="Vyměnit fotku">
          <div className="draft-modal__card">
            <header>
              <div>
                <p className="eyebrow">Výměna fotky</p>
                <h2>{activePage.title}</h2>
              </div>
              <button type="button" onClick={() => setActivePageId(null)}>Zavřít</button>
            </header>
            <div className="draft-photo-picker">
              {project.photos.map((photo) => (
                <button key={photo.id} type="button" onClick={() => assignPhotoToPage(activePage.id, photo.id)}>
                  {previewUrls[photo.id] ? <img src={previewUrls[photo.id]} alt={photo.fileName} /> : <span>Lokální fotka není dostupná</span>}
                  <strong>{photo.fileName}</strong>
                  <small>Score {photo.score}</small>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
